import { useMemo, type ReactElement } from 'react';

import {
  PortfolioRebalancingRulesDocument,
  RebalancingRuleTemplatesDocument,
  useCreateRebalancingRuleInputSuspenseQuery,
  useCreateRebalancingRuleMutation,
} from '../../../../generated/graphql.tsx';
import { convertDateInUtcToUTCISODate, parseUtcDate } from '../../../date.utils.ts';
import { useNavigate } from 'react-router';
import { useFeedback } from '../../../technical/Feedback/UseFeedback.tsx';
import isNil from 'lodash/fp/isNil';
import RebalancingRuleForm, { type FormInputFields, type FormOutputFields } from './RebalancingRuleForm.tsx';

const CreateRebalancingRuleForm = ({
  portfolioDefinitionId,
  redirectAfterCreation,
  defaultValues,
}: {
  portfolioDefinitionId?: string;
  redirectAfterCreation: string;
  defaultValues?: FormInputFields;
}): ReactElement => {
  const { data } = useCreateRebalancingRuleInputSuspenseQuery({
    variables: {
      portfolioDefinitionId: portfolioDefinitionId ?? '',
    },
    skip: isNil(portfolioDefinitionId),
  });

  const textDate = data?.portfolio.definition.rebalancedDetails?.since;
  const since = useMemo(() => (textDate ? parseUtcDate(textDate) : null), [textDate]);

  const [createRebalancingRule] = useCreateRebalancingRuleMutation();
  const { showSuccessMessage } = useFeedback();
  const navigate = useNavigate();

  const handleFormSubmit = async ({
    input,
    onErrorAndThrow,
  }: {
    input: FormOutputFields;
    onErrorAndThrow: (error: unknown) => void;
  }): Promise<void> => {
    try {
      const rule = input.rebalancingRule;
      await createRebalancingRule({
        variables: {
          input: {
            name: input.name,
            portfolioDefinitionId: portfolioDefinitionId ?? null,
            startAt: convertDateInUtcToUTCISODate(rule.start),
            endAt: null,
            periodUnit: rule.periodUnit,
            periodValue: rule.periodValue,
            maxRebalancingCost: null,
            tradingFee: (rule.tradingFee / 100).toString(),
          },
        },
        refetchQueries: [RebalancingRuleTemplatesDocument, PortfolioRebalancingRulesDocument],
      });

      showSuccessMessage(`New rebalancing rule ${portfolioDefinitionId ? '' : 'template'} added`);

      navigate(redirectAfterCreation);
    } catch (e) {
      onErrorAndThrow(e);
    }
  };

  return <RebalancingRuleForm defaultValues={defaultValues} since={since ?? undefined} onSubmit={handleFormSubmit} />;
};

export default CreateRebalancingRuleForm;
